<template>
  <!-- The same overlapping faces as a pair in the collection. -->
  <div class="HandshakePairFaces" :aria-hidden="seatLabel ? undefined : 'true'">
    <component
      :is="isSeatButton(face) ? 'button' : 'span'"
      v-for="face in shown"
      :key="face.key"
      class="face"
      :class="[
        { 'is-empty': face.empty, 'is-open': face.open, 'is-tap': isSeatButton(face) },
        face.ring ? `has-ring is-${face.ring}` : '',
      ]"
      v-bind="isSeatButton(face) ? { type: 'button', 'aria-label': seatLabel } : { 'aria-hidden': 'true' }"
      @click="isSeatButton(face) && emit('seat')"
    >
      <img v-if="face.src" :src="face.src" alt="" />
      <GamIcon v-else :name="GamIconName.PAWN_EMPTY" size="22px" is-current />
    </component>
  </div>
</template>

<script setup lang="ts">
import { gambits } from '@/core/gambits';
import { useAuthStore } from '@/stores/auth.store';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

export interface PairFace {
  key: string;
  /** A profile photo hash. */
  image?: string | null;
  /** Nobody sits there yet, and nobody can be added from here. */
  empty?: boolean;
  /** Nobody sits there yet, and tapping adds someone: the clock's empty seat. */
  open?: boolean;
  /** The colour this player has, as a ring under the face. */
  ring?: 'white' | 'black';
}

const props = defineProps<{
  /** The other player's photo. */
  otherImage?: string | null;
  /** Nobody sits opposite yet. */
  empty?: boolean;
  /** Nobody sits opposite yet, and a code is there to fill the seat. */
  openSeat?: boolean;
  /** The other player made the offer, so they stand first. */
  otherFirst?: boolean;
  /** Both faces given outright, in order. Otherwise me and the other player. */
  faces?: PairFace[];
  /** Names the open seat as a button of its own. Left out where the faces already sit inside one. */
  seatLabel?: string;
}>();

const emit = defineEmits<{ seat: [] }>();

const isSeatButton = (face: PairFace): boolean => !!props.seatLabel && !!face.open;

const { userDetails } = storeToRefs(useAuthStore());

const imageSrc = (hash?: string | null) => (hash ? gambits.imageService.getImageSrc(hash, 100) : null);

const shown = computed((): Array<PairFace & { src: string | null }> => {
  if (props.faces) {
    return props.faces.map((face) => ({ ...face, src: face.empty || face.open ? null : imageSrc(face.image) }));
  }

  const me = { key: 'me', src: imageSrc(userDetails.value?.profileImageBlobHash) };
  const seatEmpty = props.empty || props.openSeat;
  const other = {
    key: 'other',
    src: seatEmpty ? null : imageSrc(props.otherImage),
    empty: !!props.empty,
    open: !!props.openSeat,
  };
  return props.otherFirst ? [other, me] : [me, other];
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

.HandshakePairFaces {
  --face: var(--pair-face, 48px);
  display: flex;
}

.face {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--face);
  height: var(--face);
  overflow: hidden;
  border-radius: 50%;
  background: var(--color-dark-500);
  color: var(--color-white);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  & + & {
    margin-left: calc(var(--face) * -0.2);
    box-shadow: 0 0 0 2px var(--color-dark-700);
  }

  &.is-empty {
    background: var(--color-not-played);
    color: var(--color-white-30);
  }

  /* A seat still to fill: the empty face, outlined. */
  &.is-open {
    border: 1px solid var(--color-pink);
    background: var(--color-not-played);
    color: var(--color-white-30);
  }

  &.is-tap {
    padding: 0;
    font: inherit;
    cursor: pointer;

    @include press;
  }

  /* The piece colour under the face. Only white shows as a ring; a light edge on black reads as white. */
  &.has-ring.is-white {
    box-shadow: 0 0 0 3px var(--color-white);
  }

  &.has-ring.is-black {
    box-shadow: 0 0 0 3px var(--color-dark-900);
  }

  &.has-ring + &.has-ring {
    margin-left: calc(var(--face) * -0.1);
  }

  /* A seat still to fill: its ring is pink rather than the piece colour, so it needs no outline of its own. */
  &.is-open.has-ring {
    border: none;
    box-shadow: 0 0 0 3px var(--color-pink);
  }
}
</style>
